import { toast } from "sonner";
import { Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ResourceDetail } from "@/types/admin";

function formatMemoryMb(mb?: number | null): string {
  if (mb == null) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

interface VmOverviewSideColumnProps {
  resource: ResourceDetail;
}

/**
 * System-info side column for the Overview tab, mirroring the client app's
 * VmOverviewSideColumn (ResourceOverviewTab.tsx).
 */
export function VmOverviewSideColumn({ resource }: VmOverviewSideColumnProps) {
  const copyVmUuid = () => {
    if (!resource.providerResourceId) {
      return;
    }
    void navigator.clipboard.writeText(resource.providerResourceId);
    toast.success("VM UUID copied");
  };

  const rows: { label: string; value: string; onCopy?: () => void }[] = [
    { label: "Hostname", value: resource.name },
    { label: "OS", value: resource.vm?.operatingSystem || "—" },
    {
      label: "VM UUID",
      value: resource.providerResourceId || "—",
      onCopy: resource.providerResourceId ? copyVmUuid : undefined,
    },
  ];

  if (resource.vm?.vcpu != null) {
    rows.push({ label: "vCPU", value: String(resource.vm.vcpu) });
  }
  rows.push({ label: "Memory", value: formatMemoryMb(resource.vm?.memoryMb) });
  if (resource.vm?.diskSizeGb != null) {
    rows.push({ label: "Disk", value: `${resource.vm.diskSizeGb} GB` });
  }

  // The product line and chip type are what resolve the pool, so they sit together.
  const placement = [resource.productLine, resource.chipType]
    .filter((part): part is string => part != null && part.trim() !== "")
    .join(" · ");
  rows.push({ label: "Product line", value: placement || "—" });
  rows.push({ label: "Pool", value: resource.poolName || "—" });

  return (
    <div className="flex flex-col gap-4">
      <div className={cn("rounded-xl border border-border/80 bg-card p-4 shadow-none")}>
        <div className="text-sm font-medium">System Information</div>
        <dl className="mt-3 space-y-2 text-sm">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex justify-between gap-3 border-b border-border/40 py-1.5 last:border-0"
            >
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd
                className={cn(
                  "group flex max-w-[60%] items-center justify-end gap-1 text-right font-mono text-xs font-medium text-foreground",
                  row.onCopy && "-mx-1 rounded px-1 transition-colors hover:bg-muted",
                )}
              >
                <span className="truncate">{row.value}</span>
                {row.onCopy && (
                  <button
                    type="button"
                    onClick={row.onCopy}
                    className="shrink-0 cursor-pointer text-muted-foreground transition-colors group-hover:text-foreground"
                    aria-label={`Copy ${row.label}`}
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
